import { Fragment, FunctionComponent, useState } from 'react'
import { Box, Button, Chip, Collapse, Divider, Link, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import { CheckCircleOutline, ExpandLess, ExpandMore, OpenInNew } from '@mui/icons-material'
import { IRecommendation, IRecommendationsResponse } from '../../../shared/services/reduction-action/reduction-action.model'
import { formatCostBand, formatPctRange, formatTonnes, formatTonnesRange, SCOPE2_BASIS_LABELS } from '../../../shared/utils/reduction-utils'

// The Recommended list: ranked actions that fit this organisation's own figures, each saying why
// it is there, what it might save as a range, what it costs and what the range assumes. Rows
// follow the Report Readiness finding row (title, impact, expandable detail, the numbers behind
// it). Recommendations are rule-based estimates and the copy never says otherwise.

const CONFIDENCE_LABELS: Record<string, string> = {
  high: 'Based on measured data',
  medium: 'Partly estimated data',
  low: 'Mostly estimated data',
}

const formatEvidenceValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' && v !== null ? Object.values(v as Record<string, unknown>).join(' ') : String(v))).join(', ')
  if (typeof value === 'number') return value.toLocaleString('en-GB', { maximumFractionDigits: 2 })
  if (typeof value === 'object') return ''
  return String(value)
}

// Sentence-case labels from the evidence keys ('fleetFuelTco2e' reads 'Fleet fuel tCO2e').
const formatEvidenceKey = (key: string): string => {
  const words = key.replace(/([A-Z])/g, ' $1').replace(/ Tco2e/g, ' tCO2e').replace(/ Pct/g, ' %').replace(/ Kwh/g, ' kWh').replace(/ M2/g, ' m2').toLowerCase().trim()
  return (words.charAt(0).toUpperCase() + words.slice(1)).replace('tco2e', 'tCO2e').replace('kwh', 'kWh')
}

const RecommendationRow: FunctionComponent<{
  recommendation: IRecommendation,
  canAdd: boolean,
  onAdd: (recommendation: IRecommendation) => void,
  onViewAction: (actionId: string) => void,
}> = ({ recommendation: rec, canAdd, onAdd, onViewAction }) => {
  const [expanded, setExpanded] = useState(false)
  const evidenceEntries = Object.entries(rec.evidence || {}).filter(([, value]) => (value !== null && typeof value !== 'object') || Array.isArray(value))

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        onClick={() => setExpanded(!expanded)}
        sx={{ px: 2.5, py: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
      >
        <Box sx={{ width: 32, flexShrink: 0, textAlign: 'center' }}>
          {rec.rank != null
            ? <Typography variant='body1' sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{rec.rank}</Typography>
            : <CheckCircleOutline sx={{ color: 'text.secondary', fontSize: 20 }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='body1' sx={{ fontWeight: 500 }}>{rec.name}</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {rec.entity?.name ? `${rec.entity.name}. ` : ''}{rec.reason.split('. ')[0]}.
          </Typography>
        </Box>
        <Stack alignItems='flex-end' spacing={0.5} sx={{ flexShrink: 0 }}>
          {rec.saving
            ? (
              <Typography variant='body2' sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                <b>{formatTonnesRange(rec.saving.minKg, rec.saving.maxKg)}CO<sub>2</sub>e a year</b>
              </Typography>
            )
            : <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>No saving claimed</Typography>}
          <Stack direction='row' spacing={0.5}>
            <Chip label={formatCostBand(rec.cost.band)} size='small' variant='outlined' />
            {rec.saving?.basis === 'market' && <Chip label='Market-based only' size='small' variant='outlined' />}
          </Stack>
        </Stack>
        {rec.alreadyInPlan
          ? <Chip label='In your plan' size='small' />
          : (
            <Button
              variant='outlined'
              size='small'
              disabled={!canAdd}
              onClick={(e) => { e.stopPropagation(); onAdd(rec) }}
            >
              <Typography variant='body2'>Add to plan</Typography>
            </Button>
          )}
        {expanded ? <ExpandLess sx={{ color: 'text.secondary' }} /> : <ExpandMore sx={{ color: 'text.secondary' }} />}
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2.5, pb: 2.5, pl: 9 }}>
          <Typography variant='body2' sx={{ maxWidth: '720px' }}>{rec.reason}</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '720px', mt: 1 }}>
            <b>What this assumes.</b> {rec.assumption}
          </Typography>
          {rec.saving && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
              Range: {formatPctRange(rec.saving.minPct, rec.saving.maxPct)} of {formatTonnes(rec.saving.poolKg)} tCO<sub>2</sub>e
              {rec.saving.basis !== 'both' && `, ${SCOPE2_BASIS_LABELS[rec.saving.basis].toLowerCase()}`}.
              {' '}{CONFIDENCE_LABELS[rec.confidence]}.
            </Typography>
          )}
          {rec.transfers.length > 0 && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
              Adds about {rec.transfers.map((t) => `${formatTonnesRange(t.minKg, t.maxKg)}CO2e of ${t.label}`).join('; ')}. Net saving about {formatTonnes(rec.netMidKg)} tCO<sub>2</sub>e a year.
            </Typography>
          )}
          {rec.saving && rec.saving.sharesPoolWith.length > 0 && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
              Shares the same emissions as {rec.saving.sharesPoolWith.join(' and ')}, so the savings do not add up.
              {rec.saving.stackedMaxKg < rec.saving.maxKg && ` After those, about ${formatTonnesRange(rec.saving.stackedMinKg, rec.saving.stackedMaxKg)}CO2e remains for this action.`}
            </Typography>
          )}
          {evidenceEntries.length > 0 && (
            <Paper variant='outlined' sx={{ mt: 1.5, maxWidth: '480px' }}>
              <Typography variant='caption' sx={{ color: 'text.secondary', px: 1.75, pt: 1, display: 'block' }}>The numbers behind this recommendation</Typography>
              <Table size='small'>
                <TableBody>
                  {evidenceEntries.map(([key, value]) => (
                    <TableRow key={key} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.secondary', width: '55%' }}>{formatEvidenceKey(key)}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatEvidenceValue(value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          <Stack direction='row' spacing={2} alignItems='center' sx={{ mt: 1.5 }}>
            {rec.source.reference && (
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                Source: {rec.source.reference}
                {rec.source.url && (
                  <>
                    {' '}
                    <Link href={rec.source.url} target='_blank' rel='noopener noreferrer' underline='hover'>
                      View <OpenInNew sx={{ fontSize: '0.8rem', verticalAlign: 'middle' }} />
                    </Link>
                  </>
                )}
              </Typography>
            )}
            <Link component='button' variant='caption' underline='hover' onClick={() => onViewAction(rec.actionId)}>
              About this action
            </Link>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  )
}

export const ReductionRecommendationsList: FunctionComponent<{
  data: IRecommendationsResponse,
  canAdd: boolean,
  onAdd: (recommendation: IRecommendation) => void,
  onViewAction: (actionId: string) => void,
}> = ({ data, canAdd, onAdd, onViewAction }) => {
  const [showInPlan, setShowInPlan] = useState(false)
  const ranked = data.recommendations.filter((r) => !r.alreadyInPlan)
  const inPlan = data.recommendations.filter((r) => r.alreadyInPlan)
  const sharedPools = data.pools.filter((p) => p.actions.length > 1)
  const periodName = data.reportPeriod.friendlyName || data.reportPeriod.calendarYear

  return (
    <Box>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2, maxWidth: '760px' }}>
        Ranked by the estimated saving on your own {periodName} figures. Each saving is a range from published guidance applied to your
        emissions, with the assumption behind it. These are starting points, not measurements, and you can change any figure you add.
      </Typography>

      {ranked.length > 0 && (
        <Paper variant='outlined'>
          {ranked.map((rec, index) => (
            <Fragment key={`${rec.actionId}-${rec.entity?.id ?? ''}`}>
              {index > 0 && <Divider />}
              <RecommendationRow recommendation={rec} canAdd={canAdd} onAdd={onAdd} onViewAction={onViewAction} />
            </Fragment>
          ))}
        </Paper>
      )}

      {sharedPools.length > 0 && (
        <Paper variant='outlined' sx={{ backgroundColor: 'lightGrey.light', p: 2, mt: 2 }}>
          <Typography variant='body2' sx={{ fontWeight: 500, mb: 0.5 }}>How these add up</Typography>
          {sharedPools.map((pool) => (
            <Typography key={pool.pool} variant='body2' sx={{ color: 'text.secondary' }}>
              {pool.actions.join(' and ')} draw on the same {formatTonnes(pool.poolKg)} tCO<sub>2</sub>e. Together they save about {formatTonnesRange(pool.combinedMinKg, pool.combinedMaxKg)}CO<sub>2</sub>e a year, not the sum of their ranges.
            </Typography>
          ))}
        </Paper>
      )}

      {inPlan.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button size='small' onClick={() => setShowInPlan(!showInPlan)} endIcon={showInPlan ? <ExpandLess /> : <ExpandMore />} sx={{ textTransform: 'none' }}>
            <Typography variant='body2'>Already in your plan ({inPlan.length})</Typography>
          </Button>
          <Collapse in={showInPlan} unmountOnExit>
            <Paper variant='outlined' sx={{ mt: 1 }}>
              {inPlan.map((rec, index) => (
                <Fragment key={`${rec.actionId}-${rec.entity?.id ?? ''}`}>
                  {index > 0 && <Divider />}
                  <RecommendationRow recommendation={rec} canAdd={false} onAdd={onAdd} onViewAction={onViewAction} />
                </Fragment>
              ))}
            </Paper>
          </Collapse>
        </Box>
      )}
    </Box>
  )
}
